import "./quillImageResize";
import "./quillFontSize";
import { useRef, useMemo, useCallback, useState, useEffect } from "react";
import ReactQuill from "./reactQuillNoFindNode";
import "react-quill/dist/quill.snow.css";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
} from "@mui/material";
import {
  Label as VariableIcon,
  Palette as PaletteIcon,
  Wallpaper as PageBgIcon,
  Image as ImageIcon,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  KeyboardArrowUp,
  KeyboardArrowDown,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

const VARIABLES = [
  { key: "clientName", labelEn: "Client name", labelHe: "שם לקוח" },
  { key: "clientPhone", labelEn: "Phone", labelHe: "טלפון" },
  { key: "currentDate", labelEn: "Date", labelHe: "תאריך" },
];

// Elementor-style: soft panel colors
const SIDEBAR_BG_DARK = "#252b33";
const SIDEBAR_BG_LIGHT = "#f0f2f5";
const PANEL_HEADER_DARK = "rgba(255,255,255,0.08)";
const PANEL_HEADER_LIGHT = "rgba(0,0,0,0.06)";
const EDITOR_BG_DARK = "#1c2128";
const EDITOR_BG_LIGHT = "#f6f8fa";
const TOOLBAR_BG_DARK = "#21262d";
const TOOLBAR_BG_LIGHT = "#e8eaed";
const TEXT_COLOR_DARK = "#e6edf3";
const TEXT_COLOR_LIGHT = "#1f2328";
const BORDER_DARK = "rgba(255,255,255,0.08)";
const BORDER_LIGHT = "rgba(0,0,0,0.08)";

const PanelSection = ({ icon: Icon, title, children, isDark }) => (
  <Box
    sx={{
      mb: 2,
      borderRadius: 2,
      overflow: "hidden",
      border: `1px solid ${isDark ? BORDER_DARK : BORDER_LIGHT}`,
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1.5,
        py: 1.25,
        gap: 1,
        bgcolor: isDark ? PANEL_HEADER_DARK : PANEL_HEADER_LIGHT,
        borderBottom: `1px solid ${isDark ? BORDER_DARK : BORDER_LIGHT}`,
      }}
    >
      {Icon && <Icon sx={{ fontSize: 18, opacity: 0.9 }} />}
      <Typography
        variant="subtitle2"
        sx={{ fontSize: "0.8rem", fontWeight: 600 }}
      >
        {title}
      </Typography>
    </Stack>
    <Box sx={{ p: 1.5 }}>{children}</Box>
  </Box>
);

const LOGO_WIDTH = 120;
const PLACEHOLDER_SRC =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/** Get first img in HTML or null */
const getFirstImageFromHtml = (html) => {
  if (!html || typeof html !== "string") return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const img = doc.querySelector("img");
    if (!img) return null;
    return {
      src: img.getAttribute("src") || "",
      style: img.getAttribute("style") || "",
      className: img.getAttribute("class") || "",
    };
  } catch {
    return null;
  }
};

/** Parse alignment from img inline style: "left" | "center" | "right" */
const getAlignFromStyle = (style) => {
  if (!style) return "left";
  if (style.includes("margin-left: auto") && style.includes("margin-right: 0"))
    return "right";
  if (
    style.includes("margin-left: auto") &&
    style.includes("margin-right: auto")
  )
    return "center";
  return "left";
};

/** For container: align -> justifyContent. For legacy single img: keep margin-based style. */
const JUSTIFY_BY_ALIGN = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
};

const ALIGN_STYLES = {
  left: "display: block; width: 120px; height: auto; max-width: 100%; object-fit: contain; margin-bottom: 16px; margin-left: 0; margin-right: auto;",
  center:
    "display: block; width: 120px; height: auto; max-width: 100%; object-fit: contain; margin-bottom: 16px; margin-left: auto; margin-right: auto;",
  right:
    "display: block; width: 120px; height: auto; max-width: 100%; object-fit: contain; margin-bottom: 16px; margin-left: auto; margin-right: 0;",
};

/** Parse alignment from container style string (justify-content: ...). */
const getAlignFromJustifyContent = (styleStr) => {
  if (!styleStr) return "left";
  const m = styleStr.match(/justify-content\s*:\s*(\S+)/);
  const jc = m ? m[1] : "";
  if (jc === "flex-end") return "right";
  if (jc === "center") return "center";
  return "left";
};

const ColorControl = ({ label, value, onColorChange, onNoColor, isRTL }) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
    <Typography
      variant="body2"
      sx={{ fontSize: "0.75rem", minWidth: isRTL ? 64 : 72 }}
    >
      {label}
    </Typography>
    <input
      type="color"
      value={value || "#000000"}
      onChange={(e) => onColorChange(e.target.value)}
      style={{
        width: 28,
        height: 28,
        padding: 0,
        border: "1px solid rgba(0,0,0,0.2)",
        borderRadius: 6,
        cursor: "pointer",
      }}
    />
    <Button
      size="small"
      variant="outlined"
      onClick={onNoColor}
      sx={{
        minWidth: 0,
        minHeight: 28,
        height: 28,
        px: 1,
        py: 0,
        fontSize: "0.7rem",
        textTransform: "none",
      }}
    >
      {isRTL ? "ללא" : "None"}
    </Button>
  </Stack>
);

const TemplateRichEditor = ({
  value,
  onChange,
  isRTL,
  minHeight = 380,
  pageBackgroundColor = null,
  onPageBackgroundChange,
  dynamicVariables,
}) => {
  const theme = useTheme();
  const quillRef = useRef(null);
  const editorAreaRef = useRef(null);
  const zoomInnerRef = useRef(null);
  const lastSelectionRef = useRef(null);
  const logoInputRef = useRef(null);
  const isDark = theme.palette.mode === "dark";
  const [pageBg, setPageBg] = useState(pageBackgroundColor);

  const effectivePageBg = pageBackgroundColor ?? pageBg;
  const setPageBgLocal = useCallback(
    (v) => {
      setPageBg(v);
      onPageBackgroundChange?.(v);
    },
    [onPageBackgroundChange],
  );

  const insertVariable = useCallback((variableKey) => {
    const text = `{{${variableKey}}}`;
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const range = quill.getSelection(true);
    const index = range?.index ?? quill.getLength();
    quill.insertText(index, text);
    quill.setSelection(index + text.length);
  }, []);

  const applyFormat = useCallback((format, value) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    quill.focus();
    const range = quill.getSelection(true);
    if (range) quill.format(format, value, "user");
  }, []);

  /** Get the logo container DIV from the editor DOM (works without blot registration). */
  const getLogoContainer = useCallback(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill?.root) return null;
    return quill.root.querySelector(".top-img-container") || null;
  }, []);

  /** Get the logo IMG element from the editor DOM. */
  const getLogoImg = useCallback(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill?.root) return null;
    return (
      quill.root.querySelector(".top-img-container img") ||
      quill.root.querySelector("img.top-img") ||
      null
    );
  }, []);

  const handleCompanyLogoClick = useCallback(() => {
    const input = logoInputRef.current;
    if (input) {
      input.value = "";
      input.click();
    }
  }, []);

  const handleCompanyLogoFileChange = useCallback(
    (e) => {
      const file = e.target?.files?.[0];
      if (!file || !file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = () => {
        const quill = quillRef.current?.getEditor?.();
        if (!quill || !reader.result) return;
        let container = quill.root.querySelector(".top-img-container");
        if (container) {
          let img = container.querySelector("img");
          if (!img) {
            img = document.createElement("img");
            img.className = "top-img";
            container.appendChild(img);
          }
          img.setAttribute("src", reader.result);
          img.setAttribute("style", ALIGN_STYLES.left);
          container.style.justifyContent = JUSTIFY_BY_ALIGN.left;
        } else {
          container = document.createElement("div");
          container.className = "top-img-container";
          container.style.display = "flex";
          container.style.justifyContent = JUSTIFY_BY_ALIGN.left;
          const img = document.createElement("img");
          img.className = "top-img";
          img.setAttribute("src", reader.result);
          img.setAttribute("style", ALIGN_STYLES.left);
          container.appendChild(img);
          quill.root.insertBefore(container, quill.root.firstChild);
        }
        onChange(quill.root.innerHTML);
      };
      reader.readAsDataURL(file);
    },
    [onChange],
  );

  const removeCompanyLogo = useCallback(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const img = getLogoImg();
    if (img) {
      img.className = "top-img";
      img.src = PLACEHOLDER_SRC;
      img.setAttribute("style", "display: none;");
      onChange(quill.root.innerHTML);
    }
  }, [getLogoImg, onChange]);

  const applyLogoAlignment = useCallback(
    (align) => {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;
      const container = getLogoContainer();
      if (container) {
        container.style.justifyContent = JUSTIFY_BY_ALIGN[align];
        onChange(quill.root.innerHTML);
      } else {
        const img = getLogoImg();
        if (img) {
          img.setAttribute("style", ALIGN_STYLES[align]);
          onChange(quill.root.innerHTML);
        }
      }
    },
    [getLogoContainer, getLogoImg, onChange],
  );

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ["bold", "italic", "underline", "strike"],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          ["link"],
          ["clean"],
        ],
      },
    }),
    [],
  );

  const formats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "header",
    "size",
    "color",
    "background",
    "list",
    "bullet",
    "align",
    "link",
    "image",
    "topImgContainer",
  ];

  const hasLogo = useMemo(() => {
    const first = getFirstImageFromHtml(value || "");
    if (!first) return false;
    if (first.src === PLACEHOLDER_SRC) return false;
    if (
      first.style.includes("display:none") ||
      first.style.includes("display: none")
    )
      return false;
    return true;
  }, [value]);

  const logoAlignment = useMemo(() => {
    const html = value || "";
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const container = doc.querySelector(".top-img-container");
      if (container) {
        const jc = container.getAttribute("style") || "";
        return getAlignFromJustifyContent(jc);
      }
    } catch (_) {}
    const first = getFirstImageFromHtml(html);
    return first ? getAlignFromStyle(first.style) : "left";
  }, [value]);

  const placeholderAddedRef = useRef(false);

  useEffect(() => {
    if (value == null) return;
    const html = value || "";
    if (html.includes("top-img-container") || html.includes("<img")) return;
    if (placeholderAddedRef.current) return;
    // אל תדרוס תוכן קיים (למשל מ-AI) – הוסף placeholder רק כשהתוכן ריק/מינימלי
    const stripped = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (stripped.length > 30) {
      placeholderAddedRef.current = true;
      return;
    }
    placeholderAddedRef.current = true;
    const placeholder = `<div class="top-img-container" style="display: flex; justify-content: flex-start;"><img class="top-img" src="${PLACEHOLDER_SRC}" alt="" style="display: none;"></div>`;
    onChange(placeholder + "<p><br></p>");
  }, [value, onChange]);

  useEffect(() => {
    if (value && (value.includes("top-img-container") || value.includes("<img"))) {
      placeholderAddedRef.current = true;
    }
  }, [value]);

  // Ensure first img always has class "top-img" (ReactQuill may replace DOM without calling blot create())
  useEffect(() => {
    const id = setTimeout(() => {
      const quill = quillRef.current?.getEditor?.();
      if (!quill?.root) return;
      const firstImg = quill.root.querySelector("img");
      if (firstImg) firstImg.classList.add("top-img");
    }, 0);
    return () => clearTimeout(id);
  }, [value]);

  const [fontSize, setFontSize] = useState(16);
  const FONT_SIZE_MIN = 8;
  const FONT_SIZE_MAX = 72;

  const [templateZoom, setTemplateZoom] = useState(100);
  const TEMPLATE_ZOOM_MIN = 50;
  const TEMPLATE_ZOOM_MAX = 150;
  const TEMPLATE_ZOOM_STEP = 10;

  // Wrap only .ql-container in a zoom wrapper so the Quill toolbar stays fixed (not zoomed)
  useEffect(() => {
    const run = () => {
      const el = editorAreaRef.current;
      if (!el) return;
      const container = el.querySelector(".ql-container.ql-snow");
      if (!container) return;

      if (!container.dataset.zoomWrapped) {
        const wrapper = document.createElement("div");
        wrapper.className = "ql-zoom-wrapper";
        wrapper.setAttribute("data-zoom-wrapper", "1");
        Object.assign(wrapper.style, {
          flex: "1",
          minHeight: "0",
          minWidth: "0",
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        });
        const inner = document.createElement("div");
        inner.className = "ql-zoom-inner";
        inner.setAttribute("data-zoom-inner", "1");
        Object.assign(inner.style, {
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
        });
        zoomInnerRef.current = inner;
        container.parentNode.insertBefore(wrapper, container);
        wrapper.appendChild(inner);
        inner.appendChild(container);
        container.dataset.zoomWrapped = "true";
      }

      const inner = zoomInnerRef.current || el.querySelector(".ql-zoom-inner");
      if (inner) {
        const pct = 10000 / templateZoom;
        inner.style.width = `${pct}%`;
        inner.style.height = `${pct}%`;
        inner.style.transform = `scale(${templateZoom / 100})`;
        inner.style.transformOrigin = "center center";
      }
    };
    const id = setTimeout(run, 0);
    return () => clearTimeout(id);
  }, [templateZoom, value]);

  useEffect(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const handler = (range) => {
      if (range) {
        lastSelectionRef.current = { index: range.index, length: range.length };
        const format = quill.getFormat(range);
        const sizeVal = format?.size;
        if (sizeVal) {
          const num = parseInt(String(sizeVal).replace(/px$/, ""), 10);
          if (!Number.isNaN(num)) setFontSize(num);
        }
      }
    };
    quill.on("selection-change", handler);
    return () => quill.off("selection-change", handler);
  }, []);

  const applyFontSize = useCallback(
    (size) => {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;
      const n = Math.max(
        FONT_SIZE_MIN,
        Math.min(FONT_SIZE_MAX, Number(size) || 16),
      );
      const sizePx = `${n}px`;
      const range = quill.getSelection() || lastSelectionRef.current;
      quill.focus();
      if (range != null && range.length > 0) {
        quill.formatText(range.index, range.length, "size", sizePx, "user");
        quill.setSelection(range.index, range.length);
        onChange(quill.root.innerHTML);
      } else {
        quill.format("size", sizePx, "user");
      }
      setFontSize(n);
    },
    [onChange],
  );

  const editorBg = effectivePageBg ?? "#ffffff";
  const isLightPage =
    effectivePageBg == null ||
    effectivePageBg === "#ffffff" ||
    effectivePageBg === "#fff";
  const editorTextColor = isLightPage ? "#1a1a1a" : isDark ? TEXT_COLOR_DARK : TEXT_COLOR_LIGHT;
  const editorPlaceholderColor = isLightPage
    ? "rgba(0,0,0,0.45)"
    : isDark
      ? "rgba(230,237,243,0.5)"
      : "rgba(31,35,40,0.5)";

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        minWidth: 0,
        height: "100%",
        minHeight: 400,
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${isDark ? BORDER_DARK : BORDER_LIGHT}`,
      }}
    >
      <input
        ref={logoInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleCompanyLogoFileChange}
      />

      {/* Left sidebar - Elementor style */}
      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          bgcolor: isDark ? SIDEBAR_BG_DARK : SIDEBAR_BG_LIGHT,
          borderRight: `1px solid ${isDark ? BORDER_DARK : BORDER_LIGHT}`,
          p: 1.5,
          overflowY: "auto",
        }}
      >
        <PanelSection
          icon={VariableIcon}
          title={isRTL ? "משתנים דינמיים" : "Dynamic variables"}
          isDark={isDark}
        >
          <Stack spacing={0.75}>
            {(dynamicVariables && dynamicVariables.length > 0 ? dynamicVariables : VARIABLES).map((v) => (
              <Button
                key={v.key}
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => insertVariable(v.key)}
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontSize: "0.8rem",
                  py: 1,
                  borderStyle: "dashed",
                  "&:hover": {
                    borderStyle: "solid",
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                {isRTL ? v.labelHe : v.labelEn}{" "}
                <Typography
                  component="span"
                  sx={{ fontFamily: "monospace", fontSize: "0.7rem", ml: 0.5 }}
                >
                  {`{{${v.key}}}`}
                </Typography>
              </Button>
            ))}
          </Stack>
        </PanelSection>

        <PanelSection
          icon={PaletteIcon}
          title={isRTL ? "צבעים" : "Colors"}
          isDark={isDark}
        >
          <ColorControl
            label={isRTL ? "צבע טקסט" : "Text"}
            onColorChange={(hex) => applyFormat("color", hex)}
            onNoColor={() => applyFormat("color", false)}
            isRTL={isRTL}
          />
          <ColorControl
            label={isRTL ? "רקע טקסט" : "Background"}
            onColorChange={(hex) => applyFormat("background", hex)}
            onNoColor={() => applyFormat("background", false)}
            isRTL={isRTL}
          />
        </PanelSection>

        <PanelSection
          icon={PageBgIcon}
          title={isRTL ? "רקע דף" : "Page background"}
          isDark={isDark}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <input
              type="color"
              value={effectivePageBg || "#ffffff"}
              onChange={(e) => setPageBgLocal(e.target.value)}
              style={{
                width: 28,
                height: 28,
                padding: 0,
                border: "1px solid rgba(0,0,0,0.2)",
                borderRadius: 6,
                cursor: "pointer",
              }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPageBgLocal(null)}
              sx={{
                minWidth: 0,
                minHeight: 28,
                height: 28,
                px: 1,
                py: 0,
                fontSize: "0.7rem",
                textTransform: "none",
              }}
            >
              {isRTL ? "ללא" : "None"}
            </Button>
          </Stack>
        </PanelSection>

        <PanelSection
          icon={ImageIcon}
          title={isRTL ? "לוגו חברה" : "Company logo"}
          isDark={isDark}
        >
          <Stack spacing={1}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<ImageIcon sx={{ fontSize: 18 }} />}
              onClick={handleCompanyLogoClick}
              sx={{ textTransform: "none", fontSize: "0.8rem", py: 1 }}
            >
              {isRTL ? "ייבא לוגו חברה" : "Import company logo"}
            </Button>
            {hasLogo && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    minWidth: isRTL ? 56 : 52,
                  }}
                >
                  {isRTL ? "יישור" : "Align"}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.25}
                  sx={{ flex: 1, justifyContent: "center" }}
                >
                  <IconButton
                    size="small"
                    onClick={() => applyLogoAlignment("left")}
                    sx={{
                      p: 0.5,
                      bgcolor:
                        logoAlignment === "left"
                          ? isDark
                            ? "rgba(255,255,255,0.12)"
                            : "rgba(0,0,0,0.08)"
                          : "transparent",
                      border: `1px solid ${logoAlignment === "left" ? (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.23)") : "transparent"}`,
                    }}
                    aria-label={isRTL ? "שמאל" : "Left"}
                  >
                    <FormatAlignLeft sx={{ fontSize: 20 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => applyLogoAlignment("center")}
                    sx={{
                      p: 0.5,
                      bgcolor:
                        logoAlignment === "center"
                          ? isDark
                            ? "rgba(255,255,255,0.12)"
                            : "rgba(0,0,0,0.08)"
                          : "transparent",
                      border: `1px solid ${logoAlignment === "center" ? (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.23)") : "transparent"}`,
                    }}
                    aria-label={isRTL ? "מרכז" : "Center"}
                  >
                    <FormatAlignCenter sx={{ fontSize: 20 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => applyLogoAlignment("right")}
                    sx={{
                      p: 0.5,
                      bgcolor:
                        logoAlignment === "right"
                          ? isDark
                            ? "rgba(255,255,255,0.12)"
                            : "rgba(0,0,0,0.08)"
                          : "transparent",
                      border: `1px solid ${logoAlignment === "right" ? (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.23)") : "transparent"}`,
                    }}
                    aria-label={isRTL ? "ימין" : "Right"}
                  >
                    <FormatAlignRight sx={{ fontSize: 20 }} />
                  </IconButton>
                </Stack>
              </Stack>
            )}
            {hasLogo && (
              <Button
                fullWidth
                variant="outlined"
                color="error"
                size="small"
                onClick={removeCompanyLogo}
                sx={{ textTransform: "none", fontSize: "0.8rem", py: 0.75 }}
              >
                {isRTL ? "הסר תמונה" : "Remove image"}
              </Button>
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1, textAlign: "center" }}
          >
            {isRTL
              ? "הלוגו יופיע במסמך, גודל קבוע"
              : "Logo appears in document, fixed size"}
          </Typography>
        </PanelSection>
      </Box>

      {/* Main editor area */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          "& .ql-toolbar.ql-snow": {
            borderColor: isDark ? BORDER_DARK : BORDER_LIGHT,
            backgroundColor: isDark ? TOOLBAR_BG_DARK : TOOLBAR_BG_LIGHT,
            borderRadius: 0,
            padding: "8px 12px",
            flexShrink: 0,
          },
          "& .quill": {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          },
          "& .ql-container.ql-snow": {
            borderColor: isDark ? BORDER_DARK : BORDER_LIGHT,
            borderTop: "none",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            width: "210mm",
            minWidth: "210mm",
            maxWidth: "210mm",
            marginLeft: "auto",
            marginRight: "auto",
            boxSizing: "border-box",
          },
          "& .ql-editor": {
            minHeight: minHeight,
            flex: 1,
            width: "100%",
            boxSizing: "border-box",
            backgroundColor: editorBg,
            color: editorTextColor,
            fontSize: "0.9375rem",
            lineHeight: 1.6,
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
            padding: "20px 24px",
          },
          "& .ql-editor.ql-blank::before": {
            color: editorPlaceholderColor,
            fontStyle: "normal",
          },
          "& .ql-editor h1, & .ql-editor h2, & .ql-editor h3, & .ql-editor h4, & .ql-editor h5, & .ql-editor h6": {
            color: editorTextColor,
          },
          "& .ql-editor p, & .ql-editor h1, & .ql-editor h2, & .ql-editor h3, & .ql-editor h4, & .ql-editor h5, & .ql-editor h6, & .ql-editor li":
            {
              position: "relative",
              ...(isRTL
                ? { paddingRight: "1.35em" }
                : { paddingLeft: "1.35em" }),
              "&:hover::before": {
                content: isRTL ? '"\\25C0"' : '"\\25B6"',
                color: "#22c55e",
                position: "absolute",
                top: 0,
                fontSize: "14px",
                lineHeight: 1.6,
                ...(isRTL ? { right: 0 } : { left: 0 }),
              },
            },
          "& .ql-editor .top-img-container": {
            maxWidth: "100%",
            display: "flex",
            alignItems: "center",
            marginBottom: "16px",
          },
          "& .ql-snow .ql-stroke": {
            stroke: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
          },
          "& .ql-snow .ql-fill": {
            fill: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
          },
          "& .ql-snow .ql-picker": {
            color: isDark ? TEXT_COLOR_DARK : TEXT_COLOR_LIGHT,
            fontSize: "13px",
          },
          "& .ql-snow .ql-picker-label": {
            paddingLeft: "6px",
            paddingRight: "6px",
            textAlign: "center",
          },
          "& .ql-toolbar button": {
            width: "28px",
            height: "28px",
            padding: "3px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          },
          "& .ql-toolbar button svg": {
            width: "18px",
            height: "18px",
          },
          "& .ql-snow .ql-toolbar button:hover .ql-stroke": {
            stroke: theme.palette.primary.main,
          },
          "& .ql-snow .ql-toolbar button.ql-active .ql-stroke": {
            stroke: theme.palette.primary.main,
          },
          "& .ql-snow .ql-toolbar button:hover .ql-fill": {
            fill: theme.palette.primary.main,
          },
          "& .ql-snow .ql-toolbar button.ql-active .ql-fill": {
            fill: theme.palette.primary.main,
          },
          "& .ql-toolbar .ql-picker-options": {
            backgroundColor: isDark ? TOOLBAR_BG_DARK : "#fff",
            color: isDark ? TEXT_COLOR_DARK : TEXT_COLOR_LIGHT,
            borderColor: isDark ? BORDER_DARK : BORDER_LIGHT,
          },
          "& .ql-snow.ql-toolbar button:hover, & .ql-snow .ql-toolbar button:focus, & .ql-snow.ql-toolbar button.ql-active":
            {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.06)",
            },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            flexShrink: 0,
            px: 1.5,
            py: 0.5,
            borderBottom: `1px solid ${isDark ? BORDER_DARK : BORDER_LIGHT}`,
            backgroundColor: isDark ? TOOLBAR_BG_DARK : TOOLBAR_BG_LIGHT,
          }}
        >
          {isRTL ? (
            <>
              <Box sx={{ flex: 1 }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                גודל גופן
              </Typography>
              <IconButton
                size="small"
                onClick={() => applyFontSize(fontSize - 1)}
                disabled={fontSize <= FONT_SIZE_MIN}
                sx={{ p: 0.25, mr: 0.5 }}
                aria-label="הקטן"
              >
                <KeyboardArrowDown sx={{ fontSize: 18 }} />
              </IconButton>
              <TextField
                type="number"
                value={fontSize}
                onChange={(e) => applyFontSize(e.target.value)}
                inputProps={{
                  min: FONT_SIZE_MIN,
                  max: FONT_SIZE_MAX,
                  step: 1,
                  style: {
                    textAlign: "center",
                    padding: "0 8px",
                    width: 52,
                    fontSize: "0.8rem",
                    height: 22,
                    boxSizing: "border-box",
                  },
                }}
                variant="outlined"
                sx={{
                  width: 52,
                  "& .MuiInputBase-root": {
                    minHeight: "22px !important",
                    height: "22px !important",
                    maxHeight: "22px !important",
                    padding: 0,
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-root": {
                    minHeight: "22px !important",
                    height: "22px !important",
                    maxHeight: "22px !important",
                    padding: 0,
                    "& fieldset": { borderRadius: 1 },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "0 8px !important",
                    height: "22px !important",
                    minHeight: "22px !important",
                    boxSizing: "border-box",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDark ? BORDER_DARK : BORDER_LIGHT,
                  },
                  "& input": {
                    color: isDark ? TEXT_COLOR_DARK : TEXT_COLOR_LIGHT,
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={() => applyFontSize(fontSize + 1)}
                disabled={fontSize >= FONT_SIZE_MAX}
                sx={{ p: 0.25, ml: 0.5 }}
                aria-label="הגדל"
              >
                <KeyboardArrowUp sx={{ fontSize: 18 }} />
              </IconButton>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ ml: 1 }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    setTemplateZoom((z) =>
                      Math.max(TEMPLATE_ZOOM_MIN, z - TEMPLATE_ZOOM_STEP),
                    )
                  }
                  disabled={templateZoom <= TEMPLATE_ZOOM_MIN}
                  sx={{ p: 0.25 }}
                  aria-label="הקטן תצוגה"
                >
                  <ZoomOutIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    minWidth: 36,
                    textAlign: "center",
                  }}
                >
                  {templateZoom}%
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    setTemplateZoom((z) =>
                      Math.min(TEMPLATE_ZOOM_MAX, z + TEMPLATE_ZOOM_STEP),
                    )
                  }
                  disabled={templateZoom >= TEMPLATE_ZOOM_MAX}
                  sx={{ p: 0.25 }}
                  aria-label="הגדל תצוגה"
                >
                  <ZoomInIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Stack>
            </>
          ) : (
            <>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ mr: 1 }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    setTemplateZoom((z) =>
                      Math.max(TEMPLATE_ZOOM_MIN, z - TEMPLATE_ZOOM_STEP),
                    )
                  }
                  disabled={templateZoom <= TEMPLATE_ZOOM_MIN}
                  sx={{ p: 0.25 }}
                  aria-label="Zoom out"
                >
                  <ZoomOutIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    minWidth: 36,
                    textAlign: "center",
                  }}
                >
                  {templateZoom}%
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    setTemplateZoom((z) =>
                      Math.min(TEMPLATE_ZOOM_MAX, z + TEMPLATE_ZOOM_STEP),
                    )
                  }
                  disabled={templateZoom >= TEMPLATE_ZOOM_MAX}
                  sx={{ p: 0.25 }}
                  aria-label="Zoom in"
                >
                  <ZoomInIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Stack>
              <Box sx={{ flex: 1 }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                Font
              </Typography>
              <IconButton
                size="small"
                onClick={() => applyFontSize(fontSize - 1)}
                disabled={fontSize <= FONT_SIZE_MIN}
                sx={{ p: 0.25, mr: 0.5 }}
                aria-label="Decrease"
              >
                <KeyboardArrowDown sx={{ fontSize: 18 }} />
              </IconButton>
              <TextField
                type="number"
                value={fontSize}
                onChange={(e) => applyFontSize(e.target.value)}
                inputProps={{
                  min: FONT_SIZE_MIN,
                  max: FONT_SIZE_MAX,
                  step: 1,
                  style: {
                    textAlign: "center",
                    padding: "0 8px",
                    width: 52,
                    fontSize: "0.8rem",
                    height: 22,
                    boxSizing: "border-box",
                  },
                }}
                variant="outlined"
                sx={{
                  width: 52,
                  "& .MuiInputBase-root": {
                    minHeight: "22px !important",
                    height: "22px !important",
                    maxHeight: "22px !important",
                    padding: 0,
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-root": {
                    minHeight: "22px !important",
                    height: "22px !important",
                    maxHeight: "22px !important",
                    padding: 0,
                    "& fieldset": { borderRadius: 1 },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "0 8px !important",
                    height: "22px !important",
                    minHeight: "22px !important",
                    boxSizing: "border-box",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDark ? BORDER_DARK : BORDER_LIGHT,
                  },
                  "& input": {
                    color: isDark ? TEXT_COLOR_DARK : TEXT_COLOR_LIGHT,
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={() => applyFontSize(fontSize + 1)}
                disabled={fontSize >= FONT_SIZE_MAX}
                sx={{ p: 0.25, ml: 0.5 }}
                aria-label="Increase"
              >
                <KeyboardArrowUp sx={{ fontSize: 18 }} />
              </IconButton>
            </>
          )}
        </Stack>
        {/* Quill: toolbar stays here (fixed). Only .ql-container is wrapped in zoom in useEffect. */}
        <Box
          ref={editorAreaRef}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value || ""}
            onChange={onChange}
            modules={modules}
            formats={formats}
            placeholder={
              isRTL
                ? "כתוב את תוכן התבנית כאן..."
                : "Write your template content here..."
            }
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TemplateRichEditor;
